import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import './Sidebar.css';

const Sidebar = ({ sidebarOpen, onNavClick, onLogout, customNavItems }) => {
  const location = useLocation();
  const currentPath = location.pathname;

  // Default navigation items for the Business Dashboard
  const defaultNavItems = [
    { 
      section: 'MAIN', 
      items: [
        { name: 'Get Started', icon: 'fa-home', path: '/get-started', active: currentPath === '/get-started' }
      ] 
    },
    { 
      section: 'DASHBOARD', 
      items: [
        { name: 'Analytics', icon: 'fa-chart-bar', path: '/dashboard', active: currentPath === '/dashboard' },
      ] 
    },
    { 
      section: 'Plans', 
      items: [
        { name: 'Plans', icon: 'fa-box', path: '/products', active: currentPath === '/products' },
        { name: 'Hmo Links', icon: 'fa-link', path: '/insurance-links', active: currentPath === '/insurance-links' }
      ] 
    },
    { 
      section: 'Agent', 
      items: [
        // ✅ FIXED: Now correctly highlights when on /agents
        { name: 'Agents', icon: 'fa-user-tie', active: currentPath === '/agents', path: '/agents' }
      ] 
    },
    { 
      section: 'DOCUMENTS', 
      items: [
        // ✅ FIXED: Now correctly highlights when on /company/documents
        { name: 'Inbox', icon: 'fa-folder-open', path: '/company/documents', active: currentPath === '/company/documents' }
      ] 
    },
    { 
      section: 'Feedback', 
      items: [
        { name: 'Feedback', icon: 'fa-comments', active: currentPath === '/feedback', path: '/feedback' }
      ] 
    }
  ];

  // Use customNavItems if passed (for Admin/Agent dashboards), otherwise use default
  const navItems = customNavItems || defaultNavItems;

  return (
    <aside className={`sidebar ${sidebarOpen ? 'sidebar-open' : ''}`}>
      <div className="sidebar-header">
        <Link to="/" className="brand-logo">
          <img src="/images/fsl_logo.png" alt="Logo" style={{ height: '50px' }} />
        </Link>
      </div>
      <nav className="sidebar-nav">
        {navItems.map((section, idx) => (
          <div key={idx} className="nav-section">
            <h6 className="nav-section-title">{section.section}</h6>
            <ul className="nav-list">
              {section.items.map((item, itemIdx) => (
                <li key={itemIdx}>
                  <Link 
                    to={item.path || '#'} 
                    className={`nav-link ${item.active ? 'active' : ''}`}
                    onClick={(e) => item.path && onNavClick && onNavClick(item.path, e)}
                  >
                    <i className={`fas ${item.icon} me-2`}></i>
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>
      <div className="sidebar-footer">
        <div className="nav-section">
          <h6 className="nav-section-title">ACCOUNT</h6>
          <ul className="nav-list">
            <button className="nav-link" onClick={onLogout}>
              <i className="fas fa-sign-out-alt me-2"></i>Logout
            </button>
          </ul>
        </div>
      </div>
       <style>{`
        /* 🔴 ORANGE SCROLLBAR STYLES */
        ::-webkit-scrollbar {
          width: 10px;
          height: 10px;
        }
        ::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }
        ::-webkit-scrollbar-thumb {
          background: #F89423;
          border-radius: 10px;
          border: 2px solid #f1f1f1;
        }
        ::-webkit-scrollbar-thumb:hover {
          background: #ea7c1f;
        }
        * {
          scrollbar-width: thin;
          scrollbar-color: #F89423 #f1f1f1;
        }

        /* Page Styles */
        .business-dashboard-page {
          display: flex;
          min-height: 100vh;
          background-color: #f8f9fa;
        }

        /* Sidebar Styles - ORANGE THEME */
        .sidebar {
          width: 260px;
          background: white;
          color: #333;
          position: fixed;
          left: 0;
          top: 0;
          height: 100vh;
          overflow-y: auto;
          transition: transform 0.3s ease;
          z-index: 1000;
          border-right: 1px solid #e5e7eb;
          border-left: 3px solid #F89423;
        }
        .sidebar-header {
          padding: 20px;
          border-bottom: 1px solid #e5e7eb;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .brand-logo {
          color: #333;
          font-size: 24px;
          font-weight: 700;
          text-decoration: none;
          display: flex;
          align-items: center;
        }
        .sidebar-nav {
          padding: 20px 0;
        }
        .nav-section {
          margin-bottom: 25px;
        }
        .nav-section-title {
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 1px;
          color: #F89423;
          padding: 0 20px;
          margin-bottom: 10px;
        }
        .nav-list {
          list-style: none;
          padding: 0;
          margin: 0;
        }
        .nav-link {
          display: flex;
          align-items: center;
          padding: 12px 20px;
          color: #374151;
          text-decoration: none;
          transition: all 0.3s ease;
          font-weight: 500;
          font-size: 14px;
          border-left: 3px solid transparent;
        }
        .nav-link i {
          width: 20px;
          margin-right: 12px;
          font-size: 16px;
          color: #6b7280;
        }
        .nav-link:hover {
          background-color: #fef3e2;
          color: #F89423;
          border-left-color: #F89423;
        }
        .nav-link:hover i {
          color: #F89423;
        }
        .nav-link.active {
          background-color: #F89423;
          color: white;
          border-left-color: #F89423;
        }
        .nav-link.active i {
          color: white;
        }
        .sidebar-footer {
          margin-top: auto;
          padding-bottom: 20px;
          border-top: 1px solid #e5e7eb;
          padding-top: 20px;
        }

        /* Main Content */
        .main-content {
          flex: 1;
          margin-left: 260px;
          display: flex;
          flex-direction: column;
          background-color: #f9fafb;
        }
        .top-header {
          background: white;
          padding: 15px 30px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.05);
          display: flex;
          justify-content: space-between;
          align-items: center;
          position: sticky;
          top: 0;
          z-index: 100;
        }
        .header-left {
          display: flex;
          align-items: center;
          gap: 20px;
        }
        .page-title {
          display: flex;
          align-items: center;
          color: #1f2937;
          font-size: 20px;
          font-weight: 600;
        }
        .header-right {
          display: flex;
          align-items: center;
          gap: 15px;
        }
        .country-selector {
          display: flex;
          align-items: center;
          padding: 8px 15px;
          background: #f3f4f6;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 500;
          gap: 8px;
        }
        .country-selector img {
          border-radius: 4px;
        }
        .header-icon {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          border: none;
          background: #f3f4f6;
          color: #6b7280;
          cursor: pointer;
          position: relative;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .header-icon:hover {
          background: #e5e7eb;
          color: #F89423;
        }
        .header-icon .badge {
          position: absolute;
          top: 5px;
          right: 5px;
          background: #F89423;
          color: white;
          font-size: 10px;
          padding: 2px 5px;
          border-radius: 10px;
          min-width: 18px;
          text-align: center;
        }
        .user-avatar {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: linear-gradient(135deg, #F89423 0%, #ea7c1f 100%);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          cursor: pointer;
          font-size: 14px;
        }

        /* Test Mode Banner */
        .test-mode-banner {
          background: #fff7ed;
          border-bottom: 1px solid #fed7aa;
          padding: 12px 30px;
          text-align: center;
          font-size: 14px;
          color: #c2410c;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
        }
        .test-mode-banner strong {
          color: #c2410c;
        }
        .test-mode-banner .btn {
          background: #F89423;
          color: white;
          border: none;
          padding: 6px 16px;
          border-radius: 6px;
          font-weight: 600;
          font-size: 13px;
          cursor: pointer;
          transition: all 0.3s ease;
        }
        .test-mode-banner .btn:hover {
          background: #ea7c1f;
        }

        /* Content Wrapper */
        .content-wrapper {
          padding: 30px;
          flex: 1;
        }

        /* Cards */
        .card {
          border-radius: 12px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
          background: white;
          border: none;
        }

        /* Responsive */
        @media (max-width: 992px) {
          .sidebar {
            transform: translateX(-100%);
          }
          .sidebar.sidebar-open {
            transform: translateX(0);
          }
          .main-content {
            margin-left: 0;
          }
        }
        @media (max-width: 768px) {
          .content-wrapper {
            padding: 15px;
          }
          .top-header {
            padding: 15px;
          }
          .header-right .country-selector span {
            display: none;
          }
        }
      `}</style>
    </aside>
    
  );
};

export default Sidebar;